<template>
    <component
        :is="numeric ? 'ol' : 'ul'"
        v-if="!pending && chapters && chapters.length"
        class="guideline-chapters"
    >
        <li
            v-for="(chapter, index) in chapters"
            :key="index"
            :class="[
                'guideline-chapters__item',
                { 'is-active': isChapterActive(chapter, index) }
            ]"
        >
            <a
                v-if="chapter.uri && chapter.title && chapter.isAnchor"
                class="guideline-chapters__title"
                :href="chapter.uri"
                @click.prevent="goToAnchor(chapter.uri)"
            >
                {{ titleCase(chapter.title) }}
            </a>

            <nuxt-link
                v-else-if="chapter.uri && chapter.title"
                :to="`/${chapter.uri}`"
                class="guideline-chapters__title"
            >
                {{ titleCase(chapter.title) }}
            </nuxt-link>

            <BaseIcon
                v-if="getSubheadingsBySlug(chapter.slug).length"
                :class="['guideline-chapters__anchor-icon', { 'is-active': isChapterActive(chapter, index) }]"
                :icon="activeSublists.includes(index) ? 'chevron-up' : 'chevron-down'"
                @click.prevent="openSublist(index)"
            />

            <BasePill
                v-if="getHighlightCountBySlug(chapter.slug)"
                element="span"
                class="pill--result"
            >
                <template v-if="getHighlightCountBySlug(chapter.slug) > 1">
                    {{ getHighlightCountBySlug(chapter.slug) }} results
                </template>
                <template v-else>
                    1 result
                </template>
            </BasePill>

            <ol
                v-if="activeSublists.includes(index) && getSubheadingsBySlug(chapter.slug).length"
                class="guideline-chapters__sublist"
            >
                <li
                    v-for="(subheading, subIndex) in getSubheadingsBySlug(chapter.slug)"
                    :key="subIndex"
                    class="guideline-chapters__subitem"
                >
                    <a
                        v-if="subheading.anchor && subheading.title"
                        class="guideline-chapters__subtitle"
                        :href="subheading.anchor"
                        @click.prevent="goToSubheading(chapter, subheading.anchor)"
                    >
                        <span class="guideline-chapters__subtitle-number">{{ parseSubheadingTitle(subheading.title).number }}</span>
                        <span class="guideline-chapters__subtitle-text">{{ titleCase(parseSubheadingTitle(subheading.title).text) }}</span>
                    </a>
                </li>
            </ol>

            <!--            <pre>-->
            <!--                &lt;!&ndash;TODO: implement subheadings UX here&ndash;&gt;-->
            <!--                {{ getSubheadingsBySlug(chapter.slug) }}-->
            <!--            </pre>-->
        </li>
    </component>

    <content-placeholders
        v-else-if="pending"
        class="guideline-chapters"
    >
        <div
            v-for="i in [1,2,3,4,5,6]"
            :key="i"
            class="guideline-chapters__item"
        >
            <content-placeholders-text :lines="1" />
        </div>
    </content-placeholders>
</template>


<script setup>
import { useGuidelineSearchStore } from '~/store/guideline-search';
import { useRoute } from 'vue-router';

const route = useRoute();

const guidelineSearchStore = useGuidelineSearchStore();

const props = defineProps({
    pending: {
        type: Boolean,
        default: false,
        required: false
    },

    numeric: {
        type: Boolean,
        default: true,
        required: false
    },

    chapters: {
        type: Array,
        required: false,
        default: null
    },
    applyTitleCase: {
        type: Boolean,
        required: false,
        default: true
    }
});

function isChapterActive(chapter, index) {
    return (
        `/${chapter.uri}` === route.path ||
        (
            index === 0 &&
            (route.name === 'guideline-slug' || route.name === 'guideline-summary')
        )
    );
}

const { chapters: storeChapters } = storeToRefs(guidelineSearchStore);

function getSubheadingsBySlug(chapterSlug) {
    const subheadings = guidelineSearchStore.getSubheadingsBySlug(chapterSlug);
    if (!subheadings || subheadings.length <= 1) {
        return [];
    }
    return subheadings.slice(1);
}

/**
 * Parse a subheading title into number prefix and text
 * e.g. "3.2. Aetiology And Risk Factors" => { number: "3.2.", text: "Aetiology And Risk Factors" }
 */
function parseSubheadingTitle(title) {
    const match = title?.match(/^([\d.]+\.?)\s*(.*)$/);
    if (match) {
        return { number: match[1], text: match[2] };
    }
    return { number: '', text: title || '' };
}

function titleCase(str) {
    if (typeof str !== 'string') {
        return str;
    }
    if (props.applyTitleCase) {
        return str.toLowerCase().split(' ').map(function(word) {
            if (!word) {
                return '';
            }
            return word.replace(word[0], word[0].toUpperCase());
        }).join(' ');
    } else {
        return str;
    }
}

function getHighlightCountBySlug(chapterSlug) {
    const chapterMeta = storeChapters.value?.find(chapter => chapter.slug === chapterSlug);

    if (!chapterMeta) {
        return;
    }

    return chapterMeta.highlightCount;
}

const activeSublists = ref([]);

// Expand active chapter's sublist on load and navigation
function expandActiveChapter() {
    if (props.chapters?.length) {
        const activeIndex = props.chapters.findIndex((chapter, index) => isChapterActive(chapter, index));
        const indexToExpand = activeIndex >= 0 ? activeIndex : 0;
        if (!activeSublists.value.includes(indexToExpand)) {
            activeSublists.value.push(indexToExpand);
        }
    }
}

onMounted(() => {
    expandActiveChapter();
});

// Update on navigation - close others and expand active
watch(() => route.path, () => {
    activeSublists.value = [];
    expandActiveChapter();
});

function openSublist(index) {
    if (activeSublists.value.includes(index)) {
        activeSublists.value = activeSublists.value.filter(i => i !== index);
    } else {
        activeSublists.value.push(index);
    }
}

function goToAnchor(id) {
    const cleanId = id.replace('#', '');
    const el = document.getElementById(cleanId);

    if (!el) {return;}

    const offset = -100;
    const elPositionY = el.offsetTop;

    window.scrollTo({ behavior: 'smooth', top: elPositionY + offset });
}

async function goToSubheading(chapter, anchor) {
    const chapterPath = `/${chapter.uri}`;
    const isOnChapter = route.path === chapterPath;

    if (isOnChapter) {
        // Already on the chapter, just scroll to anchor
        goToAnchor(anchor);
    } else {
        // Navigate to chapter with anchor hash - chapter.vue handles scroll after load
        await navigateTo({ path: chapterPath, hash: anchor });
    }
}
</script>

<style lang="less">
.guideline-chapters {
    display: none;
    margin: 0;
    padding: .25rem 1rem;
    border-radius: var(--borderRadius-xs);
    background: var(--color-gray-200);

    @media @q-md-min {
        display: block;
    }

    @media @q-lg-min {
        padding: .5rem 1.75rem;
    }

    @media screen and ( min-width: @screen-md ) and ( min-height: 720px ) {
        position: sticky;
        top: 6rem;
        align-self: start;

        [data-searching='true'] & {
            top: 12.5rem;
        }
    }
}

ul.guideline-chapters {
    li:before {
        content: none;
        display: none;
    }

    li {
        padding-left: 0;
    }
}

.guideline-chapters__item {
    position: relative;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    margin: 0;
    padding: 0 0 0 2rem;
    line-height: 1;
    border-bottom: 1px solid var(--color-black-10);

    &:last-child {
        border-bottom: 0;
    }

    a, &:before {
        .typography-small();
        line-height: 1.3;
        color: var(--color-secondary-dark);
        text-decoration: none;
        opacity: .8;
    }

    a:hover {
        color: var(--color-primary);
    }

    .vue-content-placeholders-text,
    .guideline-chapters__title {
        flex: 1 1 auto;
        //display: inline;
        transition: opacity var(--transition-fast);
        line-height: 1.3;

        padding-top: 1rem;
        padding-bottom: 1rem;
        padding-right: 1.5rem;
    }


    .guideline-chapters__subtitle {
        padding-top: .5rem;
        padding-bottom: .5rem;
    }

    .pill {
        white-space: nowrap;
        justify-self: flex-end;
        align-self: center;
    }

    &:active,
    &:hover {
        a, &:before {
            opacity: 1;
        }
    }

    &:active {
        > a, &:before {
            color: var(--color-primary);
        }
    }

    &:before {
        top: auto;
        left: .625rem;
        line-height: 1.4;
        padding-top: 1rem;
        padding-bottom: 1rem;
    }

    @media @q-lg-min {
        &:before {
            left: .625rem;
        }
    }
}

.guideline-chapters__title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.guideline-chapters__anchor-icon {
    position: absolute;
    right: -1rem;
    padding: 1.125rem;
    transition: transform .2s ease;
    cursor: pointer;
    font-size: 1rem;
}

.guideline-chapters__anchor-icon.is-active {
    color: var(--color-primary);
}

.guideline-chapters__anchor-icon svg path {
    stroke-width: 4px;
}

.guideline-chapters__item.is-active {
    &:before {
        font-weight: 700;
        color: var(--color-primary);
        opacity: 1;
    }

    > .guideline-chapters__title {
        font-weight: 700;
        color: var(--color-primary);
        opacity: 1;
    }

    > .guideline-chapters__title .guideline-chapters__anchor-icon svg {
        transform: rotate(180deg) scale(1.05);
    }
}

.guideline-chapters__sublist {
    margin-top: 0;
    list-style: none;
    margin-bottom: 1rem;
}

.guideline-chapters__sublist li:before {
    content: none;
}

li.guideline-chapters__subitem {
    padding-left: 0;
    margin-bottom: 0;
}

.guideline-chapters__subtitle {
    display: flex;
    gap: 0.5rem;
    color: var(--color-secondary-dark);
}

.guideline-chapters__subtitle-number {
    width: 4ch;
    flex-shrink: 0;
}

.guideline-chapters__subtitle-text {
    flex: 1;
}
</style>
